import KeenSlider from 'keen-slider';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const MONTH_NAMES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const DAY_NAMES = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab', 'Dom'];
const ANIMATED_FLAG = 'eventsArchiveAnimationsInit';

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const toArray = (input) => {
  if (!input) {
    return [];
  }

  return Array.isArray(input) ? input.filter(Boolean) : [input];
};

const setWillChange = (elements, value = 'transform, opacity') => {
  toArray(elements).forEach((element) => {
    if (element && element.style) {
      element.style.willChange = value;
    }
  });
};

const clearWillChange = (elements) => {
  setWillChange(elements, 'auto');
};

const revealSection = (selector, options = {}) => {
  const sections = document.querySelectorAll(selector);

  if (!sections.length) {
    return;
  }

  sections.forEach((section) => {
    if (!section || section.dataset.eventsAnimated === 'true') {
      return;
    }

    const fromVars = {
      y: 42,
      opacity: 0,
      ...options.from,
    };

    const toVars = {
      y: 0,
      opacity: 1,
      duration: 1,
      ease: 'power3.out',
      ...options.to,
      scrollTrigger: {
        trigger: section,
        start: 'top 82%',
        once: true,
        markers: false,
        ...(options.scrollTrigger || {}),
      },
      onStart: () => setWillChange(section),
      onComplete: () => {
        clearWillChange(section);
        section.dataset.eventsAnimated = 'true';
      },
    };

    gsap.fromTo(section, fromVars, toVars);
  });
};

const revealChildren = (sectionSelector, childSelector, options = {}) => {
  const sections = document.querySelectorAll(sectionSelector);

  if (!sections.length) {
    return;
  }

  sections.forEach((section) => {
    if (!section || section.dataset.eventsChildrenAnimated === 'true') {
      return;
    }

    const children = section.querySelectorAll(childSelector);
    if (!children.length) {
      return;
    }

    gsap.fromTo(
      children,
      {
        y: 38,
        opacity: 0,
        scale: 0.985,
        ...options.from,
      },
      {
        y: 0,
        opacity: 1,
        scale: 1,
        duration: 0.95,
        ease: 'power3.out',
        stagger: 0.1,
        ...options.to,
        scrollTrigger: {
          trigger: section,
          start: 'top 82%',
          once: true,
          markers: false,
          ...(options.scrollTrigger || {}),
        },
        onStart: () => setWillChange(children),
        onComplete: () => {
          clearWillChange(children);
          section.dataset.eventsChildrenAnimated = 'true';
        },
      }
    );
  });
};

const animateListItems = (container, options = {}) => {
  if (!container) {
    return;
  }

  const items = container.querySelectorAll('.events-archive__item');
  if (!items.length) {
    return;
  }

  gsap.fromTo(
    items,
    {
      y: 24,
      opacity: 0,
      ...options.from,
    },
    {
      y: 0,
      opacity: 1,
      stagger: 0.06,
      duration: 0.72,
      ease: 'power3.out',
      ...options.to,
      onStart: () => setWillChange(items),
      onComplete: () => clearWillChange(items),
    }
  );
};

const showWithoutAnimation = () => {
  const selectors = [
    '.events-archive__hero-title',
    '.events-archive__hero-text',
    '.events-archive__toggle',
    '.events-archive__featured',
    '.events-archive__featured-card',
    '.events-archive__calendar',
    '.events-archive__filters',
    '.events-archive__list',
    '.events-archive__item',
    '.previous-events__header',
    '.previous-events__feedback-badge',
    '.previous-events__center',
    '.previous-events__metric',
  ];

  gsap.set(selectors.join(', '), {
    clearProps: 'all',
    opacity: 1,
    y: 0,
    scale: 1,
  });
};

const initEventsArchiveAnimations = () => {
  if (!document.body || !document.querySelector('.events-archive') || document.body.dataset[ANIMATED_FLAG] === 'true') {
    return;
  }

  document.body.dataset[ANIMATED_FLAG] = 'true';

  if (prefersReducedMotion()) {
    showWithoutAnimation();
    return;
  }

  revealSection('.events-archive__hero-title, .events-archive__hero-text', {
    from: { y: 34, opacity: 0 },
    to: { duration: 0.95 },
    scrollTrigger: { trigger: '.events-archive__hero', start: 'top 90%' },
  });

  revealSection('.events-archive__toggle', {
    from: { y: 30, opacity: 0 },
    to: { duration: 0.85 },
  });

  revealSection('.events-archive__calendar, .events-archive__filters', {
    from: { y: 40, opacity: 0 },
    to: { duration: 0.95 },
  });

  revealSection('.events-archive__list', {
    from: { y: 36, opacity: 0 },
    to: { duration: 0.9 },
  });

  revealChildren('.events-archive__list', '.events-archive__item', {
    from: { y: 28, opacity: 0, scale: 0.99 },
    to: { duration: 0.8, stagger: 0.08 },
  });

  revealSection('.previous-events__header', {
    from: { y: 34, opacity: 0 },
    to: { duration: 0.9 },
  });

  revealChildren('.previous-events', '.previous-events__feedback-badge, .previous-events__center, .previous-events__metric', {
    from: { y: 34, opacity: 0, scale: 0.99 },
    to: { duration: 0.95, stagger: 0.09 },
  });

  window.setTimeout(() => {
    ScrollTrigger.refresh();
  }, 250);
};

const initFeaturedSlider = () => {
  const root = document.querySelector('[data-events-featured-slider]');
  if (!root) {
    return;
  }

  const sliderElement = root.querySelector('.keen-slider');
  if (!sliderElement) {
    return;
  }

  const prev = document.querySelector('[data-events-featured-prev]');
  const next = document.querySelector('[data-events-featured-next]');
  const totalSlides = sliderElement.querySelectorAll('.keen-slider__slide').length;

  const updateCenterSlideClass = (sliderInstance) => {
    const relIndex = sliderInstance.track.details?.rel ?? 0;
    sliderInstance.slides.forEach((slide, index) => {
      slide.classList.toggle('is-center', index === relIndex);
    });
  };

  if (totalSlides <= 1) {
    if (prev) prev.disabled = true;
    if (next) next.disabled = true;
    return;
  }

  const slider = new KeenSlider(sliderElement, {
    loop: true,
    mode: 'snap',
    renderMode: 'performance',
    slides: { perView: 1, spacing: 0, origin: 'center' },
    created(instance) {
      updateCenterSlideClass(instance);
    },
    slideChanged(instance) {
      updateCenterSlideClass(instance);
    },
    breakpoints: {     
      '(min-width: 1200px)': {
        slides: { perView: 1.1, spacing: 18, origin: 'center' },
      },
    },
  });

  if (prev) prev.addEventListener('click', () => slider.prev());
  if (next) next.addEventListener('click', () => slider.next());
};

const formatDateKey = (year, month, day) => {
  const monthNormalized = String(month).padStart(2, '0');
  const dayNormalized = String(day).padStart(2, '0');
  return `${year}-${monthNormalized}-${dayNormalized}`;
};

const escapeHtml = (text) =>
  String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const formatMonthLabel = (month) => MONTH_NAMES[month - 1] || '';

const renderCalendar = (root, state) => {
  const { month, year, eventsMap } = state;
  const firstDay = new Date(year, month - 1, 1);
  const startWeekDay = (firstDay.getDay() + 6) % 7;
  const daysInPrevMonth = new Date(year, month - 1, 0).getDate();
  const daysInMonth = new Date(year, month, 0).getDate();
  const monthLabel = formatMonthLabel(month);

  let html = '<div class="events-archive__calendar-panel">';
  html += '<div class="events-archive__calendar-panel-head">';
  html += `<p class="events-archive__calendar-title">Eventos do mês de ${monthLabel}</p>`;
  html += '<div class="events-archive__calendar-panel-nav">';
  html += '<button type="button" data-calendar-prev-inner aria-label="Mês anterior"></button>';
  html += '<button type="button" data-calendar-next-inner aria-label="Próximo mês"></button>';
  html += '</div></div>';
  html += '<div class="events-archive__calendar-weekdays">';
  DAY_NAMES.forEach((dayName) => {
    html += `<span>${dayName}</span>`;
  });
  html += '</div><div class="events-archive__calendar-days">';

  for (let index = 0; index < 42; index += 1) {
    const dayOffset = index - startWeekDay;

    let cellYear = year;
    let cellMonth = month;
    let dayNumber = dayOffset + 1;
    let isMuted = false;

    if (dayOffset < 0) {
      isMuted = true;
      dayNumber = daysInPrevMonth + dayOffset + 1;
      cellMonth = month - 1;
      if (cellMonth < 1) {
        cellMonth = 12;
        cellYear -= 1;
      }
    } else if (dayOffset >= daysInMonth) {
      isMuted = true;
      dayNumber = dayOffset - daysInMonth + 1;
      cellMonth = month + 1;
      if (cellMonth > 12) {
        cellMonth = 1;
        cellYear += 1;
      }
    }

    const dateKey = formatDateKey(cellYear, cellMonth, dayNumber);
    const eventsForDate = eventsMap[dateKey] || [];
    const firstEvent = eventsForDate[0] || null;
    const colorClass = firstEvent?.color ? ` events-archive__calendar-cell--${firstEvent.color}` : '';
    const eventTitle = firstEvent?.title ? `<span class="events-archive__calendar-event-label">${escapeHtml(firstEvent.title)}</span>` : '';
    const dot = firstEvent ? `<span class="events-archive__calendar-dot events-archive__calendar-dot--${firstEvent.color}" aria-hidden="true"></span>` : '';
    const mutedClass = isMuted ? ' events-archive__calendar-cell--muted' : '';

    html += `<div class="events-archive__calendar-cell${mutedClass}${colorClass}">`;
    html += `<span class="events-archive__calendar-day-number">${dayNumber}</span>`;
    html += `${dot}${eventTitle}`;
    html += '</div>';
  }

  html += '</div></div>';
  root.innerHTML = html;

  const monthLabelElement = document.querySelector('[data-calendar-current-month]');
  if (monthLabelElement) {
    monthLabelElement.textContent = monthLabel;
  }
};

const initCalendar = () => {
  const root = document.querySelector('[data-events-calendar]');
  if (!root) {
    return;
  }

  const initialMonth = Number(root.getAttribute('data-initial-month')) || new Date().getMonth() + 1;
  const initialYear = Number(root.getAttribute('data-initial-year')) || new Date().getFullYear();
  const rawEvents = root.getAttribute('data-events') || '{}';

  let eventsMap = {};
  try {
    eventsMap = JSON.parse(rawEvents);
  } catch (error) {
    eventsMap = {};
  }

  const state = {
    month: initialMonth,
    year: initialYear,
    eventsMap,
  };

  const prev = document.querySelector('[data-calendar-prev]');
  const next = document.querySelector('[data-calendar-next]');
  const goPrev = () => {
    state.month -= 1;
    if (state.month < 1) {
      state.month = 12;
      state.year -= 1;
    }
    renderCalendar(root, state);
  };
  const goNext = () => {
    state.month += 1;
    if (state.month > 12) {
      state.month = 1;
      state.year += 1;
    }
    renderCalendar(root, state);
  };

  if (prev) {
    prev.addEventListener('click', goPrev);
  }

  if (next) {
    next.addEventListener('click', goNext);
  }

  root.addEventListener('click', (event) => {
    const target = event.target.closest('button');
    if (!target) return;
    if (target.hasAttribute('data-calendar-prev-inner')) {
      goPrev();
    }
    if (target.hasAttribute('data-calendar-next-inner')) {
      goNext();
    }
  });

  renderCalendar(root, state);
};

const initEventsArchiveFilters = () => {
  const form = document.querySelector('.events-archive__filters-form');
  const listSection = document.querySelector('.events-archive__list');

  if (!form || !listSection || !window.rvEventsArchive?.ajaxUrl || !window.rvEventsArchive?.nonce) {
    return;
  }

  const submitButton = form.querySelector('.events-archive__submit');
  const createSkeletonMarkup = (count = 6) => {
    const items = Array.from({ length: count })
      .map(
        () => `
          <article class="events-archive__item events-archive__item--skeleton" aria-hidden="true">
            <div class="events-archive__item-content">
              <span class="events-archive__skeleton-chip"></span>
              <span class="events-archive__skeleton-line events-archive__skeleton-line--sm"></span>
              <span class="events-archive__skeleton-line events-archive__skeleton-line--lg"></span>
              <span class="events-archive__skeleton-line events-archive__skeleton-line--md"></span>
            </div>
          </article>
        `
      )
      .join('');

    return `<div class="events-archive__grid events-archive__grid--skeleton">${items}</div>`;
  };

  const setLoading = (isLoading) => {
    if (submitButton) {
      submitButton.disabled = isLoading;
    }
    listSection.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    listSection.classList.toggle('is-loading', isLoading);
    if (isLoading) {
      listSection.innerHTML = createSkeletonMarkup();
    }
  };

  const buildQueryString = (params) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        searchParams.set(key, String(value));
      }
    });
    return searchParams.toString();
  };

  const requestList = async (page = 1) => {
    const formData = new FormData(form);
    const requestParams = {
      period: formData.get('period') || 'upcoming',
      search: formData.get('search') || '',
      event_category_filter: formData.get('event_category_filter') || '',
      paged: page,
    };

    const body = new URLSearchParams();
    body.set('action', 'rv_events_archive_filter');
    body.set('nonce', window.rvEventsArchive.nonce);
    body.set('period', requestParams.period);
    body.set('search', requestParams.search);
    body.set('event_category_filter', requestParams.event_category_filter);
    body.set('paged', String(requestParams.paged));

    const emptyTextNode = listSection.querySelector('.events-archive__empty');
    if (emptyTextNode?.textContent) {
      body.set('empty_text', emptyTextNode.textContent.trim());
    }

    setLoading(true);
    try {
      const response = await fetch(window.rvEventsArchive.ajaxUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
        body: body.toString(),
      });

      if (!response.ok) {
        throw new Error('Request failed');
      }

      const payload = await response.json();
      if (!payload?.success || typeof payload?.data?.html !== 'string') {
        throw new Error('Invalid payload');
      }

      listSection.innerHTML = payload.data.html;
      if (!prefersReducedMotion()) {
        animateListItems(listSection);
      }
      const queryString = buildQueryString(requestParams);
      const baseUrl = form.getAttribute('action') || window.location.pathname;
      const nextUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl;
      window.history.pushState({}, '', nextUrl);
      ScrollTrigger.refresh();
    } catch (error) {
      const queryString = buildQueryString(requestParams);
      const baseUrl = form.getAttribute('action') || window.location.pathname;
      window.location.assign(queryString ? `${baseUrl}?${queryString}` : baseUrl);
    } finally {
      setLoading(false);
    }
  };

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    requestList(1);
  });

  listSection.addEventListener('click', (event) => {
    const paginationLink = event.target.closest('.events-archive__pagination a');
    if (!paginationLink) {
      return;
    }

    event.preventDefault();
    const href = paginationLink.getAttribute('href') || '';
    const url = new URL(href, window.location.origin);
    const page = Number(url.searchParams.get('paged') || 1);
    requestList(Number.isFinite(page) && page > 0 ? page : 1);
  });
};

const initEventsArchive = () => {
  initFeaturedSlider();
  initCalendar();
  initEventsArchiveFilters();
  initEventsArchiveAnimations();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initEventsArchive);
} else {
  initEventsArchive();
}
